import { AnalyticsBannerLink } from '@/shared/features/analytics/presentation/components';

interface SpotlightSectionProps {
  data: Record<string, unknown> & {
    title?: string[];
    bgImageUrl?: string;
    titleImageUrl?: string;
    scissorImageUrl?: string;
    ribbonImageUrl?: string;
    link?: string;
    lazyload?: string;
    blankImageUrl?: string;
    shouldRefresh?: boolean;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

export default function SpotlightSection({
  data,
  sectionKey: _sectionKey,
  eventLabel: _eventLabel,
  priority: _priority,
}: SpotlightSectionProps) {
  const title = (data.title || []) as string[];
  const imageUrl = data.bgImageUrl || '';
  const titleImageUrl = data.titleImageUrl || '';
  const scissorImageUrl = data.scissorImageUrl || '';
  const ribbonImageUrl = data.ribbonImageUrl || '';
  const link = data.link || '#';
  const lazyload = data.lazyload;

  if (!imageUrl) return null;

  const ariaLabel = title.length ? title.join(' ') : 'Spotlight Banner';

  const loadingMode = lazyload ? 'lazy' : 'eager';

  const baseOverlay =
    'absolute top-1/2 -translate-y-1/2 h-auto pointer-events-none transform-gpu';
  const ease = 'ease-[cubic-bezier(0.4,0,0.2,1)]';

  const Image = (
    <div className="relative w-full overflow-hidden">
      <img
        src={imageUrl}
        alt={ariaLabel}
        className="block w-full h-auto"
        loading={loadingMode}
        decoding="async"
      />

      {/* Overlay stack (same structure as Bluestone markup) */}
      {titleImageUrl && (
        <img
          className={[
            baseOverlay,
            'left-0 w-[72%] z-[3]',
            `transition-transform duration-[2000ms] ${ease}`,
            'motion-reduce:transition-none motion-reduce:transform-none',
            'translate-x-0 group-data-[inview=true]:translate-x-[-120%]',
          ].join(' ')}
          src={titleImageUrl}
          alt=""
          loading={loadingMode}
          decoding="async"
        />
      )}
      {scissorImageUrl && (
        <img
          className={[
            baseOverlay,
            'left-[66.5%] w-[12%] z-[2]',
            `transition-transform duration-[1500ms] ${ease}`,
            'motion-reduce:transition-none motion-reduce:transform-none',
            'translate-x-0 group-data-[inview=true]:translate-x-[300%]',
          ].join(' ')}
          src={scissorImageUrl}
          alt=""
          loading={loadingMode}
          decoding="async"
        />
      )}
      {ribbonImageUrl && (
        <img
          className={[
            baseOverlay,
            'left-[72%] w-[28%] z-[1]',
            `transition-transform duration-[2000ms] ${ease}`,
            'motion-reduce:transition-none motion-reduce:transform-none',
            'translate-x-0 group-data-[inview=true]:translate-x-[120%]',
          ].join(' ')}
          src={ribbonImageUrl}
          alt=""
          loading={loadingMode}
          decoding="async"
        />
      )}
    </div>
  );

  const Clickable = (
    <AnalyticsBannerLink
      to={link}
      bannerName={ariaLabel}
      bannerType="banner"
      fromNavBar={false}
      className="block"
      aria-label={ariaLabel}
      title={title?.[0] || 'Spotlight'}
      shouldRefresh={data.shouldRefresh}
    >
      {Image}
    </AnalyticsBannerLink>
  );

  return (
    <section className="hidden md:block">
      {Clickable}
    </section>
  );
}

